import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useBlogs } from '../../features/blogs/hooks/useBlogs';
import { useCourses } from '../../features/series/hooks/useCourses';
import { Icon, IconName } from './Icon';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';

type ResultKind = 'series' | 'post' | 'course';

interface Result {
  key: string;
  kind: ResultKind;
  title: string;
  context: string;
  to: string;
}

const KIND_LABEL: Record<ResultKind, string> = {
  series: 'Series',
  post: 'Posts',
  course: 'Courses',
};

const KIND_ICON: Record<ResultKind, IconName> = {
  series: 'book-open',
  post: 'file-text',
  course: 'sparkles',
};

const MAX_PER_KIND = 6;

function matches(haystack: string, needle: string) {
  return haystack.toLowerCase().includes(needle);
}

/**
 * ⌘K palette. Searches across everything already on the client — series,
 * their posts, and courses — so it needs no new endpoint and stays instant.
 */
export function SearchPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const { blogs } = useBlogs();
  const { courses } = useCourses();

  const totalPosts = useMemo(
    () => blogs.reduce((n, b) => n + (b.posts?.length ?? 0), 0),
    [blogs]
  );

  // With an empty box, suggest real destinations rather than describing the box.
  const suggestions = useMemo<Result[]>(() => {
    const courseHits: Result[] = courses.slice(0, 3).map((course) => ({
      key: `course-${course.id}`,
      kind: 'course',
      title: course.title,
      context: 'Course',
      to: `/series/${course.slug}`,
    }));
    const seriesHits: Result[] = blogs.slice(0, 4).map((blog) => ({
      key: `series-${blog.id}`,
      kind: 'series',
      title: blog.title,
      context: `${blog.posts?.length ?? 0} chapters · ${blog.category?.name ?? 'Uncategorised'}`,
      to: `/blog/${blog.slug}`,
    }));
    return [...courseHits, ...seriesHits];
  }, [blogs, courses]);

  const matched = useMemo<Result[]>(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];

    const series: Result[] = [];
    const posts: Result[] = [];
    const courseHits: Result[] = [];

    for (const blog of blogs) {
      const haystack = [blog.title, blog.description, blog.category?.name, ...(blog.tags ?? [])]
        .filter(Boolean)
        .join(' ');
      if (matches(haystack, q) && series.length < MAX_PER_KIND) {
        series.push({
          key: `series-${blog.id}`,
          kind: 'series',
          title: blog.title,
          context: `${blog.posts?.length ?? 0} chapters · ${blog.category?.name ?? 'Uncategorised'}`,
          to: `/blog/${blog.slug}`,
        });
      }
      for (const post of blog.posts ?? []) {
        if (posts.length >= MAX_PER_KIND) break;
        if (matches(post.title, q)) {
          posts.push({
            key: `post-${post.id}`,
            kind: 'post',
            title: post.title,
            context: blog.title,
            to: `/blog/${blog.slug}/${post.slug}`,
          });
        }
      }
    }

    for (const course of courses) {
      const haystack = [course.title, course.description, ...(course.tags ?? [])]
        .filter(Boolean)
        .join(' ');
      if (matches(haystack, q) && courseHits.length < MAX_PER_KIND) {
        courseHits.push({
          key: `course-${course.id}`,
          kind: 'course',
          title: course.title,
          context: 'Course',
          to: `/series/${course.slug}`,
        });
      }
    }

    return [...courseHits, ...series, ...posts];
  }, [query, blogs, courses]);

  const isSearching = query.trim().length > 0;
  const results = isSearching ? matched : suggestions;

  // Reset when the palette opens
  useEffect(() => {
    if (open) {
      setQuery('');
      setCursor(0);
      // focus after the element is actually in the DOM
      const id = window.requestAnimationFrame(() => inputRef.current?.focus());
      return () => window.cancelAnimationFrame(id);
    }
  }, [open]);

  useEffect(() => setCursor(0), [query]);

  useBodyScrollLock(open);

  // Keep the highlighted row in view
  useEffect(() => {
    listRef.current
      ?.querySelector('[data-active="true"]')
      ?.scrollIntoView({ block: 'nearest' });
  }, [cursor]);

  if (!open) return null;

  const go = (result: Result) => {
    onClose();
    navigate(result.to);
    window.scrollTo({ top: 0 });
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setCursor((c) => (results.length ? (c + 1) % results.length : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setCursor((c) => (results.length ? (c - 1 + results.length) % results.length : 0));
    } else if (e.key === 'Enter' && results[cursor]) {
      e.preventDefault();
      go(results[cursor]);
    }
  };

  let renderedKind: ResultKind | null = null;

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-stretch justify-center bg-[var(--color-overlay)] backdrop-blur-sm sm:items-start sm:px-4 sm:pt-[10vh]"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Search jslipi"
        onKeyDown={onKeyDown}
        className="flex h-full w-full flex-col overflow-hidden border-border bg-surface-container-lowest shadow-2xl sm:h-auto sm:max-w-[720px] sm:rounded-3xl sm:border"
      >
        {/* Query */}
        <div className="flex h-16 shrink-0 items-center gap-3 border-b border-border px-4 sm:h-[66px] sm:px-5">
          <Icon name="search" size={20} className="shrink-0 text-muted-foreground" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search jslipi"
            aria-label="Search posts, series and courses"
            className="min-w-0 flex-1 bg-transparent text-[17px] font-medium text-foreground placeholder:text-muted-foreground focus:outline-none sm:text-lg"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery('')}
              aria-label="Clear search"
              className="grid h-9 w-9 shrink-0 place-items-center rounded-full text-muted-foreground hover:bg-surface-container hover:text-foreground"
            >
              <Icon name="x" size={16} />
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            aria-label="Close search"
            className="shrink-0 rounded-md border border-border px-2 py-1 text-[11px] font-semibold text-muted-foreground sm:pointer-events-none"
          >
            esc
          </button>
        </div>

        {/* Results */}
        <div
          ref={listRef}
          className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-2 sm:max-h-[52vh] sm:flex-none"
        >
          {isSearching && results.length === 0 ? (
            <div className="px-4 py-8 text-center flex flex-col items-center gap-2">
              <p className="text-[15px] font-semibold text-foreground">
                Nothing for &ldquo;{query.trim()}&rdquo;
              </p>
              <p className="text-sm text-muted-foreground">
                Try a broader word — or ask for it, every request gets read.
              </p>
            </div>
          ) : (
            <>
            {results.map((result, i) => {
              const showHeading = result.kind !== renderedKind;
              renderedKind = result.kind;
              const active = i === cursor;
              return (
                <div key={result.key}>
                  {showHeading && (
                    <div className="px-[18px] pt-3.5 pb-1.5 text-[11px] font-bold tracking-[0.12em] uppercase text-muted-foreground">
                      {KIND_LABEL[result.kind]}
                    </div>
                  )}
                  <button
                    type="button"
                    data-active={active}
                    onMouseEnter={() => setCursor(i)}
                    onClick={() => go(result)}
                    className={`flex min-h-[56px] w-full items-center gap-3.5 rounded-xl px-[18px] py-3 text-left transition-colors sm:min-h-0 ${
                      active ? 'bg-primary-fixed' : 'hover:bg-surface-container'
                    }`}
                  >
                    <Icon
                      name={KIND_ICON[result.kind]}
                      size={18}
                      className={active ? 'text-primary shrink-0' : 'text-muted-foreground shrink-0'}
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block text-[15px] font-semibold text-foreground truncate">
                        {result.title}
                      </span>
                      <span className="block text-[13px] text-muted-foreground truncate">
                        {result.context}
                      </span>
                    </span>
                    {active && (
                      <kbd className="hidden rounded-md border border-border px-1.5 py-0.5 text-[11px] font-semibold text-muted-foreground sm:block">
                        ↵
                      </kbd>
                    )}
                  </button>
                </div>
              );
            })}
            </>
          )}
        </div>

        {/* Footer hints */}
        <div className="hidden h-[52px] shrink-0 items-center gap-4 border-t border-border bg-background px-5 text-[12px] font-semibold text-muted-foreground sm:flex">
          <span className="flex items-center gap-1.5">
            <kbd className="border border-border rounded-md px-1.5 py-0.5">↑</kbd>
            <kbd className="border border-border rounded-md px-1.5 py-0.5">↓</kbd>
            navigate
          </span>
          <span className="flex items-center gap-1.5">
            <kbd className="border border-border rounded-md px-1.5 py-0.5">↵</kbd>
            open
          </span>
          <span className="ml-auto">
            {isSearching && results.length > 0 ? `${results.length} results` : `${blogs.length} series · ${totalPosts} posts · ${courses.length} courses`}
          </span>
        </div>
      </div>
    </div>,
    document.body
  );
}
